import { createContext, useContext, useState, useCallback, ReactNode } from "react";

export interface User {
  id: string;
  email: string;
  nickname: string;
  avatar: string;
  plan: "free" | "fan" | "superfan";
  planLabel: string;
  planColor: string;
  tokens: number;
  totalXP: number;
  joinedAt: string;
  favoriteGroup: string;
  bio: string;
}

interface AuthContextType {
  user: User | null;
  isLoggedIn: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const MOCK_USER: User = {
  id: "user-001",
  email: "starfan@example.com",
  nickname: "StarFan_Minjee",
  avatar: "https://public.readdy.ai/ai/img_res/f3780f8d8354268ea2a4feed2b1eb989.jpg",
  plan: "fan",
  planLabel: "Fan",
  planColor: "#ff6eb4",
  tokens: 87,
  totalXP: 31450,
  joinedAt: "2025-09-14",
  favoriteGroup: "AURORA",
  bio: "AURORA 광팬! AI로 뮤직비디오 신 제작하는 걸 좋아해요 :)",
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);

  const login = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    // Mock login - any @-containing email + 8+ char password works
    if (!email.includes("@")) {
      return { success: false, error: "올바른 이메일을 입력하세요" };
    }
    if (password.length < 8) {
      return { success: false, error: "비밀번호는 8자 이상이어야 합니다" };
    }
    // Simulate network delay
    await new Promise((res) => setTimeout(res, 800));
    setUser({ ...MOCK_USER, email });
    return { success: true };
  }, []);

  const logout = useCallback(() => {
    setUser(null);
  }, []);

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : prev));
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoggedIn: !!user, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
